import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Icon } from "semantic-ui-react";
import "../../styles/scss/danceCamp26Shift.scss";
import { useLoginCheck } from "../../hooks/useLoginCheck";

const DEFAULT_ROOMS = ["Studio A", "Studio B", "Studio C"];

// 初期値の特別企画レッスン (state で動的に移動可能)
const INITIAL_SPECIAL_LESSONS = [
  {
    label: "特別レッスン ①",
    start: "15:00",
    end: "16:30",
    instructors: ["REINA"],
    theme: "見つめ直す - ベーシック",
    accent: "pink",
  },
  {
    label: "特別レッスン ②",
    start: "11:00",
    end: "13:30",
    instructors: ["RINA", "NANAKO", "RUPPI"],
    theme: "コラボWS ストリートダンス",
    accent: "violet",
  },
];

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function DanceCamp26ShiftPage() {
  useLoginCheck();

  const [applications, setApplications] = useState([]);
  const [confirmed, setConfirmed] = useState([]);
  const [manualPending, setManualPending] = useState([]); // 手動作成された未確定レッスン
  const [instructorImageMap, setInstructorImageMap] = useState({});
  const [instructorList, setInstructorList] = useState([]); // フォームの select 用
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);
  const [selectedPendingId, setSelectedPendingId] = useState(null);
  const [selectedSpecialIdx, setSelectedSpecialIdx] = useState(null);
  const [specialLessons, setSpecialLessons] = useState(INITIAL_SPECIAL_LESSONS);
  const [extraRooms, setExtraRooms] = useState([]);
  // フォームモーダル: { open: bool, mode: 'create' | 'edit', lesson: lessonObj | null }
  const [formModal, setFormModal] = useState({ open: false, mode: "create", lesson: null });

  const fetchLessons = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/get_dance_camp_26_lessons/`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("読み込み失敗");
      const data = await res.json();
      setApplications(data.applications || []);
      setConfirmed(data.confirmed || []);
      setManualPending(data.manual_pending || []);
    } catch (e) {
      console.error(e);
      alert("レッスンの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  // インストラクター画像 (id → main_image) + select 用一覧
  const fetchInstructorImages = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/get_instructors/`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const list = await res.json();
      const arr = Array.isArray(list) ? list : [];
      const map = {};
      arr.forEach((ins) => {
        if (ins.main_image) {
          map[ins.id] = ins.main_image;
          // dancer_name / username でも引けるようにする (lesson.instructor_name 等の照合用)
          if (ins.dancer_name) map[ins.dancer_name] = ins.main_image;
          if (ins.username) map[ins.username] = ins.main_image;
        }
      });
      setInstructorImageMap(map);
      setInstructorList(arr);
    } catch (e) {
      console.error("インストラクター画像の取得に失敗:", e);
    }
  }, []);

  useEffect(() => {
    fetchLessons();
    fetchInstructorImages();
  }, [fetchLessons, fetchInstructorImages]);

  // ESC で選択解除
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedPendingId(null);
        setSelectedSpecialIdx(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const getInstructorImage = (lesson) =>
    instructorImageMap[lesson.instructor_id] ||
    instructorImageMap[lesson.instructor_name] ||
    null;

  const postLesson = async (body) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await fetch(`${apiUrl}/api/confirm_dance_camp_26_lesson/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error || "更新に失敗しました");
    }
  };

  const confirmLesson = async (lesson, startTime, roomLabel) => {
    try {
      setSubmittingId(lesson.id);
      await postLesson({
        lesson_id: lesson.id,
        start_time: startTime,
        room_label: roomLabel,
      });
      setSelectedPendingId(null);
      await fetchLessons();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmittingId(null);
    }
  };

  const releaseLesson = async (lesson) => {
    if (!window.confirm(`${lesson.instructor_name}さんのシフトを解除しますか？`)) {
      return;
    }
    try {
      setSubmittingId(lesson.id);
      await postLesson({ lesson_id: lesson.id, start_time: null });
      await fetchLessons();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmittingId(null);
    }
  };

  // 新規レッスン作成
  const createLesson = async (payload) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await fetch(`${apiUrl}/api/create_dance_camp_26_lesson/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error || "レッスンの作成に失敗しました");
    }
    return data;
  };

  // レッスン属性更新
  const updateLesson = async (payload) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await fetch(`${apiUrl}/api/update_dance_camp_26_lesson/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error || "レッスンの更新に失敗しました");
    }
    return data;
  };

  // レッスン削除（論理削除）
  const deleteLesson = async (lesson) => {
    if (
      !window.confirm(
        `「${lesson.theme}」（${lesson.instructor_name}）を削除しますか？`
      )
    ) {
      return;
    }
    try {
      setSubmittingId(lesson.id);
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/delete_dance_camp_26_lesson/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lesson_id: lesson.id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "削除に失敗しました");
      }
      await fetchLessons();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmittingId(null);
    }
  };

  const openCreateForm = () => {
    setFormModal({ open: true, mode: "create", lesson: null });
  };
  const openEditForm = (lesson) => {
    setFormModal({ open: true, mode: "edit", lesson });
  };
  const closeForm = () => {
    setFormModal({ open: false, mode: "create", lesson: null });
  };

  const updateRoom = async (lesson, roomLabel) => {
    try {
      setSubmittingId(lesson.id);
      await postLesson({
        lesson_id: lesson.id,
        start_time: lesson.start_time,
        room_label: roomLabel,
      });
      setConfirmed((prev) =>
        prev.map((slot) => ({
          ...slot,
          lessons: slot.lessons.map((l) =>
            l.id === lesson.id ? { ...l, room_label: roomLabel } : l
          ),
        }))
      );
    } catch (e) {
      alert(e.message);
      await fetchLessons();
    } finally {
      setSubmittingId(null);
    }
  };

  // ----- 派生データ -----
  const confirmedLessonIds = useMemo(() => {
    const set = new Set();
    confirmed.forEach((slot) =>
      slot.lessons.forEach((l) => set.add(l.id))
    );
    return set;
  }, [confirmed]);

  // ペンディング(各レッスン1回ずつ) — 申込由来 + 手動作成由来
  const pendingLessons = useMemo(() => {
    const map = new Map();
    applications.forEach((slot) => {
      slot.lessons.forEach((l) => {
        if (!confirmedLessonIds.has(l.id) && !map.has(l.id)) {
          map.set(l.id, l);
        }
      });
    });
    // 手動作成（start_times=[]）の pending を追加
    manualPending.forEach((l) => {
      if (!confirmedLessonIds.has(l.id) && !map.has(l.id)) {
        map.set(l.id, l);
      }
    });
    return Array.from(map.values());
  }, [applications, manualPending, confirmedLessonIds]);

  // ISO → ms (NaN 除外)。フォーマットが UTC / JST 混在しても同一論理時刻として束ねる
  const toTs = (iso) => {
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? null : t;
  };

  // 全タイムスロット — 30 分刻みで生成 (申込/確定/特別の最小〜最大範囲を覆う)
  const allTimeSlots = useMemo(() => {
    const knownTs = [];
    const knownMap = new Map(); // ts -> 元 iso (フォーマット維持用)
    const collect = (iso) => {
      const ts = toTs(iso);
      if (ts === null) return;
      knownTs.push(ts);
      if (!knownMap.has(ts)) knownMap.set(ts, iso);
    };
    applications.forEach((s) => collect(s.start_time));
    confirmed.forEach((s) => collect(s.start_time));

    if (knownTs.length === 0) return [];

    // 日付・タイムゾーン抽出 (任意の既存 iso をサンプルに)
    const sampleIso = knownMap.values().next().value;
    const datePart = sampleIso.substring(0, 10);
    const tzPart = sampleIso.substring(19) || "+09:00";

    // 特別レッスン start/end の ts も範囲計算に含める
    specialLessons.forEach((sp) => {
      const sTs = toTs(`${datePart}T${sp.start}:00${tzPart}`);
      const eTs = toTs(`${datePart}T${sp.end}:00${tzPart}`);
      if (sTs !== null) knownTs.push(sTs);
      if (eTs !== null) knownTs.push(eTs);
    });

    const HALF_HOUR = 30 * 60 * 1000;
    let minTs = Math.min(...knownTs);
    let maxTs = Math.max(...knownTs);
    // 30 分刻みに丸める
    minTs = Math.floor(minTs / HALF_HOUR) * HALF_HOUR;
    maxTs = Math.ceil(maxTs / HALF_HOUR) * HALF_HOUR;

    const pad = (n) => String(n).padStart(2, "0");
    const slots = [];
    for (let t = minTs; t < maxTs; t += HALF_HOUR) {
      // 既存 iso があればそれを使い、無ければ生成
      if (knownMap.has(t)) {
        slots.push(knownMap.get(t));
      } else {
        const d = new Date(t);
        slots.push(
          `${datePart}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${tzPart}`
        );
      }
    }
    return slots;
  }, [applications, confirmed]);

  // ISO 文字列から JST HH:MM を取得 (Date 経由でローカル時刻)
  const extractHHMM = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // xx:00 で始まるか (右側に 30 分マーカーが来る)
  const isAtHour = (iso) => {
    const d = new Date(iso);
    return !isNaN(d.getTime()) && d.getMinutes() === 0;
  };

  // 特別レッスンの span (30 分単位)
  const getSpecialSpan = (sp) => {
    const [sh, sm] = sp.start.split(":").map(Number);
    const [eh, em] = sp.end.split(":").map(Number);
    return Math.max(1, Math.ceil((eh * 60 + em - (sh * 60 + sm)) / 30));
  };

  // 特別レッスンの配置情報 (列開始 + 何列分span + idx)
  const specialPlacements = useMemo(() => {
    const list = [];
    specialLessons.forEach((sp, idx) => {
      const startIdx = allTimeSlots.findIndex(
        (t) => extractHHMM(t) === sp.start
      );
      if (startIdx < 0) return;
      let span = 1;
      for (let i = startIdx + 1; i < allTimeSlots.length; i++) {
        if (extractHHMM(allTimeSlots[i]) < sp.end) {
          span += 1;
        } else {
          break;
        }
      }
      list.push({ sp, idx, startIdx, span });
    });
    return list;
  }, [allTimeSlots, specialLessons]);

  // 特別レッスンが占有している列の index
  const coveredIndices = useMemo(() => {
    const set = new Set();
    specialPlacements.forEach(({ startIdx, span }) => {
      for (let i = startIdx; i < startIdx + span; i++) set.add(i);
    });
    return set;
  }, [specialPlacements]);

  // 部屋一覧 (デフォルト + 確定済みで使われている + 追加分)
  const rooms = useMemo(() => {
    const set = new Set(DEFAULT_ROOMS);
    confirmed.forEach((s) =>
      s.lessons.forEach((l) => l.room_label && set.add(l.room_label))
    );
    extraRooms.forEach((r) => set.add(r));
    return Array.from(set);
  }, [confirmed, extraRooms]);

  // セルマップ: { ts -> room -> lesson } (ms キー)
  const cellMap = useMemo(() => {
    const m = {};
    confirmed.forEach((slot) => {
      const ts = toTs(slot.start_time);
      if (ts === null) return;
      m[ts] = m[ts] || {};
      slot.lessons.forEach((l) => {
        const room = l.room_label || "__unassigned__";
        m[ts][room] = l;
      });
    });
    return m;
  }, [confirmed]);

  // レッスンの占有スロット数（duration_minutes を 30 分単位で切り上げ。未設定なら2）
  const lessonSpan = (lesson) => {
    const dm = lesson?.duration_minutes;
    if (!dm || isNaN(dm)) return 2;
    return Math.max(1, Math.ceil(dm / 30));
  };

  // 各 (room) で確定レッスンが占有する後続スロットの index 集合（先頭セル以外を隠すため）
  const lessonCoveredByRoom = useMemo(() => {
    const m = {};
    allTimeSlots.forEach((t, i) => {
      const map = cellMap[toTs(t)];
      if (!map) return;
      Object.entries(map).forEach(([room, lesson]) => {
        const span = lessonSpan(lesson);
        if (!m[room]) m[room] = new Set();
        for (let k = 1; k < span; k++) {
          if (i + k < allTimeSlots.length) m[room].add(i + k);
        }
      });
    });
    return m;
  }, [allTimeSlots, cellMap]);

  // 未割当 (room_label が空) の確定レッスン (ts キー)
  const unassignedByTime = useMemo(() => {
    const m = {};
    confirmed.forEach((slot) => {
      const ts = toTs(slot.start_time);
      if (ts === null) return;
      const without = slot.lessons.filter((l) => !l.room_label);
      if (without.length > 0) m[ts] = without;
    });
    return m;
  }, [confirmed]);

  const selectedPending = useMemo(
    () => pendingLessons.find((l) => l.id === selectedPendingId) || null,
    [pendingLessons, selectedPendingId]
  );

  // 選択中の pending が「カバー」する 30 分サブスロットの ts Set
  // (各 start_time t について [t, t+30min) と [t+30min, t+60min) を有効範囲とする)
  const selectedPendingCoverageSet = useMemo(() => {
    if (!selectedPending) return new Set();
    const set = new Set();
    const HALF = 30 * 60 * 1000;
    (selectedPending.start_times || []).forEach((s) => {
      const ts = toTs(s);
      if (ts === null) return;
      set.add(ts);
      set.add(ts + HALF);
    });
    return set;
  }, [selectedPending]);

  const isCellEligible = (time, room) => {
    if (!selectedPending) return false;
    const ts = toTs(time);
    if (ts === null) return false;
    if (cellMap[ts]?.[room]) return false;

    // 選択中の pending レッスンの span 分連続して空きが必要
    const span = lessonSpan(selectedPending);
    const idx = allTimeSlots.indexOf(time);
    if (idx < 0 || idx + span > allTimeSlots.length) return false;
    for (let k = 1; k < span; k++) {
      const nTs = toTs(allTimeSlots[idx + k]);
      if (nTs !== null && cellMap[nTs]?.[room]) return false;
    }

    // Studio A の特別レッスン（ハードコード state 由来）と重ならないこと
    if (room === "Studio A") {
      for (let k = 0; k < span; k++) {
        if (coveredIndices.has(idx + k)) return false;
      }
    }

    // 申込由来の場合：申込時間枠内であることをチェック（手動作成は枠なしなので任意）
    const isManual =
      !selectedPending.start_times || selectedPending.start_times.length === 0;
    if (!isManual) {
      for (let k = 0; k < span; k++) {
        const subTs = toTs(allTimeSlots[idx + k]);
        if (subTs === null || !selectedPendingCoverageSet.has(subTs)) {
          return false;
        }
      }
    }
    return true;
  };

  const handleCellClick = (time, room) => {
    if (!selectedPending) return;
    const ts = toTs(time);
    if (ts === null) return;
    if (cellMap[ts]?.[room]) return;
    if (!isCellEligible(time, room)) return;
    confirmLesson(selectedPending, time, room);
  };

  // ===== 特別レッスン 移動 =====
  const selectedSpecial =
    selectedSpecialIdx !== null ? specialLessons[selectedSpecialIdx] : null;

  const isSpecialDropEligible = (timeIdx) => {
    if (selectedSpecial === null) return false;
    const span = getSpecialSpan(selectedSpecial);
    if (timeIdx + span > allTimeSlots.length) return false;
    for (let i = timeIdx; i < timeIdx + span; i++) {
      const t = allTimeSlots[i];
      // Studio A の確定済みレッスンと重なる
      if (cellMap[toTs(t)]?.["Studio A"]) return false;
      // 自分以外の特別レッスンと重なる
      const conflict = specialPlacements.some(
        (p) =>
          p.idx !== selectedSpecialIdx &&
          i >= p.startIdx &&
          i < p.startIdx + p.span
      );
      if (conflict) return false;
    }
    return true;
  };

  const handleSelectSpecial = (idx) => {
    setSelectedSpecialIdx((prev) => (prev === idx ? null : idx));
    setSelectedPendingId(null); // 同時選択は不可
  };

  const handleMoveSpecial = (timeIdx) => {
    if (selectedSpecial === null) return;
    if (!isSpecialDropEligible(timeIdx)) return;
    const span = getSpecialSpan(selectedSpecial);
    const startTs = toTs(allTimeSlots[timeIdx]);
    if (startTs === null) return;
    const endTs = startTs + span * 30 * 60 * 1000;
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (ts) => {
      const d = new Date(ts);
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setSpecialLessons((prev) =>
      prev.map((s, i) =>
        i === selectedSpecialIdx
          ? { ...s, start: fmt(startTs), end: fmt(endTs) }
          : s
      )
    );
    setSelectedSpecialIdx(null);
  };

  // ----- ドラッグスクロール -----
  const scrollRef = useRef(null);
  const dragRef = useRef({ active: false, moved: false, startX: 0, startLeft: 0 });

  const handleMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    // インタラクティブ要素上ではドラッグしない
    if (
      e.target.closest(
        "button, select, input, .lesson-card, .tt-empty.eligible, .special-card, .lc-actions"
      )
    ) {
      return;
    }
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.pageX,
      startLeft: el.scrollLeft,
    };
    el.classList.add("is-grabbing");
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const el = scrollRef.current;
      if (!el) return;
      const dx = e.pageX - dragRef.current.startX;
      if (Math.abs(dx) > 4) dragRef.current.moved = true;
      el.scrollLeft = dragRef.current.startLeft - dx;
      e.preventDefault();
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      const el = scrollRef.current;
      if (el) el.classList.remove("is-grabbing");
      // ドラッグした場合は次の click を抑止
      if (dragRef.current.moved) {
        const block = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        document.addEventListener("click", block, { capture: true, once: true });
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const handleAddRoom = () => {
    const name = window.prompt("追加する部屋の名前を入力してください", "");
    if (name && name.trim()) {
      setExtraRooms((prev) => [...prev, name.trim()]);
    }
  };

  const totalConfirmed = confirmedLessonIds.size;
  const totalPending = pendingLessons.length;

  return (
    <div className="dance-camp-26-shift-page">
      {/* header */}
      <div className="page-header">
        <div className="title-block">
          <p className="eyebrow">Dance Camp 2026 · Shift</p>
          <h1>シフト確定 タイムテーブル</h1>
        </div>
        <div className="header-meta">
          <span className="stat-chip confirmed">
            Confirmed <span className="num">{totalConfirmed}</span>
          </span>
          <span className="stat-chip pending">
            Pending <span className="num">{totalPending}</span>
          </span>
          <button
            className={`reload-btn ${loading ? "loading" : ""}`}
            onClick={fetchLessons}
            disabled={loading}
          >
            <Icon name="refresh" />
            Reload
          </button>
        </div>
      </div>

      {/* selection hint */}
      {selectedSpecial ? (
        <div className="selection-hint">
          <span className="hi-eyebrow">Move</span>
          <span>
            <strong style={{ color: "#fff" }}>{selectedSpecial.label}</strong>{" "}
            ({selectedSpecial.instructors.join(" / ")}) の移動先を Studio A の点滅セルからクリック
          </span>
          <span
            className="hi-action"
            onClick={() => setSelectedSpecialIdx(null)}
          >
            選択解除 ✕
          </span>
        </div>
      ) : selectedPending ? (
        <div className="selection-hint">
          <span className="hi-eyebrow">Selected</span>
          <span>
            <strong style={{ color: "#fff" }}>
              {selectedPending.instructor_name}
            </strong>{" "}
            さん ({selectedPending.genre_display}) を、青く点滅しているセルに配置できます
          </span>
          <span
            className="hi-action"
            onClick={() => setSelectedPendingId(null)}
          >
            選択解除 ✕
          </span>
        </div>
      ) : (
        pendingLessons.length > 0 && (
          <div className="selection-hint">
            <span className="hi-eyebrow">How to</span>
            <span>
              申込カードまたは特別レッスンをクリック → 点滅セルをクリックで配置・移動
            </span>
          </div>
        )
      )}

      {/* timetable */}
      <div className="timetable-panel">
        <div className="panel-header">
          <p className="section-title">
            <span className="ja">タイムテーブル</span>
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="room-add" onClick={openCreateForm}>
              <Icon name="plus" />
              新規レッスン追加
            </button>
            <button className="room-add" onClick={handleAddRoom}>
              <Icon name="plus" />
              部屋を追加
            </button>
          </div>
        </div>
        <div
          className="panel-body grab-scroll"
          ref={scrollRef}
          onMouseDown={handleMouseDown}
        >
          {allTimeSlots.length === 0 ? (
            <div className="empty-state">タイムスロットがありません</div>
          ) : (
            <div
              className="timetable"
              style={{ "--col-count": allTimeSlots.length }}
            >
              {/* head */}
              <div className="tt-row tt-head">
                <div className="tt-cell tt-room-label" style={{ background: "transparent" }}>
                  <span className="ro-en">Time</span>
                </div>
                {allTimeSlots.map((t) => (
                  <div
                    className={`tt-cell ${isAtHour(t) ? "at-hour" : "at-half"}`}
                    key={t}
                  >
                    <div>
                      <div className="time-main">{fmtTime(t)}</div>
                      <div className="time-sub">{fmtDate(t)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* room rows */}
              {rooms.map((room, ri) => {
                const isStudioA = room === "Studio A";
                return (
                  <div
                    className={`tt-row ${isStudioA ? "tt-studio-a" : ""}`}
                    key={room}
                  >
                    <div className="tt-cell tt-room-label" style={{ gridColumn: 1 }}>
                      <span className="ro-en">
                        Room {String.fromCharCode(65 + ri)}
                      </span>
                      <span className="ro-name">{room}</span>
                    </div>
                    {allTimeSlots.map((t, i) => {
                      // Studio A の特別レッスン占有列はスキップ
                      if (isStudioA && coveredIndices.has(i)) return null;
                      // 1 時間レッスンの後半 30 分セルはスキップ
                      if (lessonCoveredByRoom[room]?.has(i)) return null;

                      const lesson = cellMap[toTs(t)]?.[room];
                      const eligible = isCellEligible(t, room);
                      const specialEligible =
                        isStudioA && isSpecialDropEligible(i);
                      const cellSpan = lesson ? lessonSpan(lesson) : 1;
                      return (
                        <div
                          className={`tt-cell ${
                            isAtHour(t) ? "at-hour" : "at-half"
                          }`}
                          key={`${room}-${t}`}
                          style={{ gridColumn: `${i + 2} / span ${cellSpan}` }}
                        >
                          {lesson ? (
                            <div className={`lesson-card${lesson.is_special ? " is-special" : ""}`}>
                              <div className="lc-thumb">
                                {getInstructorImage(lesson) ? (
                                  <img
                                    src={getInstructorImage(lesson)}
                                    alt={lesson.instructor_name}
                                    loading="lazy"
                                  />
                                ) : (
                                  <Icon name="user" />
                                )}
                              </div>
                              <div className="lc-body">
                                <span
                                  className={`lc-genre genre-${lesson.genre}`}
                                >
                                  {lesson.is_special
                                    ? lesson.special_label || "特別"
                                    : lesson.genre_display}
                                </span>
                                <div className="lc-name">
                                  {lesson.instructor_name}
                                </div>
                                <div className="lc-theme">{lesson.theme}</div>
                              </div>
                              <div className="lc-actions">
                                <button
                                  title="編集"
                                  onClick={() => openEditForm(lesson)}
                                  disabled={submittingId === lesson.id}
                                >
                                  <Icon name="edit" />
                                </button>
                                <button
                                  className="danger"
                                  title="シフト解除"
                                  onClick={() => releaseLesson(lesson)}
                                  disabled={submittingId === lesson.id}
                                >
                                  <Icon name="undo" />
                                </button>
                                <button
                                  className="danger"
                                  title="削除"
                                  onClick={() => deleteLesson(lesson)}
                                  disabled={submittingId === lesson.id || lesson.paid_count > 0}
                                >
                                  <Icon name="trash" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`tt-empty ${
                                eligible ? "eligible" : ""
                              } ${specialEligible ? "eligible-special" : ""}`}
                              onClick={() => {
                                if (specialEligible) handleMoveSpecial(i);
                                else handleCellClick(t, room);
                              }}
                            >
                              {eligible
                                ? "Place"
                                : specialEligible
                                ? "Move"
                                : ""}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Studio A に特別レッスンを span でオーバーレイ */}
                    {isStudioA &&
                      specialPlacements.map(({ sp, idx, startIdx, span }) => {
                        const isSelected = selectedSpecialIdx === idx;
                        return (
                          <div
                            key={`sp-${idx}`}
                            className="tt-cell tt-special-slot"
                            style={{
                              gridColumn: `${startIdx + 2} / span ${span}`,
                            }}
                          >
                            <div
                              className={`special-card accent-${sp.accent} ${
                                isSelected ? "is-selected" : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectSpecial(idx);
                              }}
                              title="クリックで移動先を選択"
                            >
                              <div className="sc-ribbon">
                                <span className="sc-num">
                                  {String(idx + 1).padStart(2, "0")}
                                </span>
                                <span className="sc-label">{sp.label}</span>
                              </div>
                            <div className="sc-time">
                              <span className="t-from">{sp.start}</span>
                              <span className="t-arrow">—</span>
                              <span className="t-to">{sp.end}</span>
                            </div>
                            <div className="sc-instructors">
                              {sp.instructors.map((name, idx) => (
                                <React.Fragment key={name}>
                                  <span className="sc-name">{name}</span>
                                  {idx < sp.instructors.length - 1 && (
                                    <span className="sc-sep">/</span>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                              <div className="sc-theme">"{sp.theme}"</div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}

              {/* 未割当 (room_label 無し) があれば最下行 */}
              {Object.keys(unassignedByTime).length > 0 && (
                <div className="tt-row">
                  <div className="tt-cell tt-room-label">
                    <span className="ro-en">No Room</span>
                    <span className="ro-name">未割当</span>
                  </div>
                  {allTimeSlots.map((t) => {
                    const list = unassignedByTime[toTs(t)] || [];
                    return (
                      <div
                        className={`tt-cell ${
                          isAtHour(t) ? "at-hour" : "at-half"
                        }`}
                        key={`unassigned-${t}`}
                      >
                        {list.length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              height: "100%",
                            }}
                          >
                            {list.map((lesson) => (
                              <div className="lesson-card" key={lesson.id}>
                                <span
                                  className={`lc-genre genre-${lesson.genre}`}
                                >
                                  {lesson.genre_display}
                                </span>
                                <div className="lc-name">
                                  {lesson.instructor_name}
                                </div>
                                <select
                                  style={{
                                    background: "rgba(11,13,26,0.7)",
                                    color: "#fff",
                                    border: "1px solid rgba(255,255,255,0.14)",
                                    borderRadius: 6,
                                    padding: "3px 6px",
                                    fontSize: 10.5,
                                  }}
                                  value=""
                                  onChange={(e) =>
                                    updateRoom(lesson, e.target.value)
                                  }
                                >
                                  <option value="" disabled>
                                    部屋を選択
                                  </option>
                                  {rooms.map((r) => (
                                    <option key={r} value={r}>
                                      {r}
                                    </option>
                                  ))}
                                </select>
                                <div className="lc-actions">
                                  <button
                                    className="danger"
                                    title="解除"
                                    onClick={() => releaseLesson(lesson)}
                                  >
                                    <Icon name="close" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="tt-empty" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* レッスン作成・編集モーダル */}
      {formModal.open && (
        <LessonFormModal
          mode={formModal.mode}
          lesson={formModal.lesson}
          instructorList={instructorList}
          onClose={closeForm}
          onSubmit={async (payload) => {
            try {
              if (formModal.mode === "create") {
                await createLesson(payload);
              } else {
                await updateLesson({
                  ...payload,
                  lesson_id: formModal.lesson.id,
                });
              }
              await fetchLessons();
              closeForm();
            } catch (e) {
              alert(e.message);
            }
          }}
        />
      )}

      {/* pending pool */}
      <div className="pending-panel">
        <div className="panel-header">
          <p className="section-title">
            <span className="ja">未確定の申込</span>
          </p>
        </div>
        {pendingLessons.length === 0 ? (
          <div className="empty-state">
            未確定の申込はありません — 全件配置済みです
          </div>
        ) : (
          <div className="pending-grid">
            {pendingLessons.map((lesson) => {
              const isSelected = selectedPendingId === lesson.id;
              const isManual =
                !lesson.start_times || lesson.start_times.length === 0;
              return (
                <div
                  key={lesson.id}
                  className={`pending-card ${isSelected ? "is-selected" : ""}${
                    lesson.is_special ? " is-special" : ""
                  }${isManual ? " is-manual" : ""}`}
                  onClick={() => {
                    setSelectedPendingId(isSelected ? null : lesson.id);
                    setSelectedSpecialIdx(null);
                  }}
                >
                  <div className="pc-head">
                    <span className={`genre-tag genre-${lesson.genre}`}>
                      {lesson.is_special
                        ? lesson.special_label || "特別"
                        : lesson.genre_display}
                    </span>
                    <span className="name">{lesson.instructor_name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditForm(lesson);
                      }}
                      style={{
                        ...roomAddBtnBase,
                        marginLeft: "auto",
                        color: "#5b9eff",
                        borderColor: "rgba(91,158,255,0.5)",
                      }}
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLesson(lesson);
                      }}
                      disabled={lesson.paid_count > 0}
                      style={{
                        ...roomAddBtnBase,
                        color: "#ff5b6b",
                        borderColor: "rgba(255,91,107,0.5)",
                        opacity: lesson.paid_count > 0 ? 0.4 : 1,
                        cursor: lesson.paid_count > 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      削除
                    </button>
                  </div>
                  {lesson.theme && (
                    <div className="pc-theme">{lesson.theme}</div>
                  )}
                  {!isManual && (
                    <div className="pc-times">
                      {(lesson.start_times || []).map((t) => (
                        <span className="time-pill" key={t}>
                          {fmtTime(t)}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="pc-foot">
                    <span>
                      {isManual
                        ? "手動追加（任意の空き枠へ）"
                        : `申込 ${(lesson.start_times || []).length} 枠`}
                    </span>
                    <span className="pc-cta">
                      {isSelected ? "選択中" : "クリックで配置"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================
// レッスン作成・編集モーダル
// =============================
function LessonFormModal({ mode, lesson, instructorList, onClose, onSubmit }) {
  const isEdit = mode === "edit";

  const initialInstructorId = lesson?.instructor_id || "";
  const initialInstructorName = lesson?.instructor_name || "";

  const [isSpecial, setIsSpecial] = useState(!!lesson?.is_special);
  const [instructorId, setInstructorId] = useState(initialInstructorId);
  const [instructorName, setInstructorName] = useState(initialInstructorName);
  const [genre, setGenre] = useState(lesson?.genre || "kpop");
  const [theme, setTheme] = useState(lesson?.theme || "");
  // 所要時間：必ず int で送る。手入力。
  const [durationStr, setDurationStr] = useState(
    String(lesson?.duration_minutes ?? 50)
  );
  const [capacityStr, setCapacityStr] = useState(
    String(lesson?.capacity ?? 30)
  );
  const [specialNumber, setSpecialNumber] = useState(
    lesson?.special_number || ""
  );
  const [specialLabel, setSpecialLabel] = useState(
    lesson?.special_label || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const paidCount = lesson?.paid_count || 0;
  const originalDuration = lesson?.duration_minutes ?? 50;
  const originalCapacity = lesson?.capacity ?? 30;

  // instructor_id を変更したら instructor_name も追従（通常・特別とも単一選択）
  useEffect(() => {
    if (!instructorId) return;
    const ins = instructorList.find((x) => String(x.id) === String(instructorId));
    if (ins) {
      setInstructorName(ins.dancer_name || ins.username || "");
    }
  }, [instructorId, instructorList]);

  const handleSubmit = async () => {
    setError("");

    // int バリデーション
    const duration = parseInt(durationStr, 10);
    if (!Number.isInteger(duration) || String(duration) !== durationStr.trim()) {
      setError("所要時間は整数で入力してください（例: 50）");
      return;
    }
    if (duration < 10 || duration > 480) {
      setError("所要時間は10〜480分の範囲で入力してください");
      return;
    }
    const capacity = parseInt(capacityStr, 10);
    if (!Number.isInteger(capacity) || String(capacity) !== capacityStr.trim()) {
      setError("定員は整数で入力してください（例: 30）");
      return;
    }
    if (capacity < 1 || capacity > 200) {
      setError("定員は1〜200の範囲で入力してください");
      return;
    }

    if (!instructorId) {
      setError("インストラクターを選択してください");
      return;
    }
    if (!instructorName.trim()) {
      setError("インストラクター名は必須です");
      return;
    }
    if (!theme.trim()) {
      setError("テーマは必須です");
      return;
    }

    // 編集時の duration 変更で申込済がある場合は確認
    if (isEdit && paidCount > 0 && duration !== originalDuration) {
      const ok = window.confirm(
        `このレッスンは${paidCount}名の申込があります。\n` +
          `所要時間を ${originalDuration}分 → ${duration}分 に変更すると、` +
          `タイムテーブル上の占有範囲が変わります。続行しますか？`
      );
      if (!ok) return;
    }

    const payload = {
      instructor_id: parseInt(instructorId, 10),
      instructor_name: instructorName.trim(),
      genre,
      theme: theme.trim(),
      duration_minutes: duration,
      capacity,
      is_special: isSpecial,
      special_number: isSpecial ? specialNumber.trim() : "",
      special_label: isSpecial ? specialLabel.trim() : "",
    };

    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (e) {
      setError(e.message || "送信に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#1b1f33",
          color: "#fff",
          width: "min(560px, 92vw)",
          maxHeight: "92vh",
          overflowY: "auto",
          borderRadius: 10,
          padding: 24,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {isEdit ? "レッスンの編集" : "新規レッスン追加"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>

        {error && (
          <div
            style={{
              background: "rgba(255,80,80,0.2)",
              color: "#ffb3b3",
              padding: "8px 12px",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* レッスンタイプ */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.75 }}>レッスンタイプ</label>
            <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
              <label style={{ fontSize: 13 }}>
                <input
                  type="radio"
                  name="lesson-type"
                  checked={!isSpecial}
                  onChange={() => setIsSpecial(false)}
                />{" "}
                通常レッスン
              </label>
              <label style={{ fontSize: 13 }}>
                <input
                  type="radio"
                  name="lesson-type"
                  checked={isSpecial}
                  onChange={() => setIsSpecial(true)}
                />{" "}
                特別レッスン
              </label>
            </div>
          </div>

          {/* インストラクター（通常・特別とも単一選択） */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.75 }}>インストラクター *</label>
            <select
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
              style={inputStyle}
            >
              <option value="">-- 選択してください --</option>
              {instructorList.map((ins) => (
                <option key={ins.id} value={ins.id}>
                  {ins.dancer_name || ins.username || `ID:${ins.id}`}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              表示名: {instructorName || "（インストラクターを選択してください）"}
            </div>
          </div>

          {/* ジャンル */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.75 }}>ジャンル *</label>
            <select
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              style={inputStyle}
            >
              <option value="kpop">K-POP</option>
              <option value="street">ストリート</option>
            </select>
          </div>

          {/* テーマ */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.75 }}>テーマ *</label>
            <input
              type="text"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="例: NewJeans - Hype Boy"
              style={inputStyle}
            />
          </div>

          {/* 所要時間 */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.75 }}>
              所要時間（分） * — 整数で入力
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={durationStr}
              onChange={(e) =>
                setDurationStr(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder="50"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              タイムテーブル上は30分単位で占有します（例: 50分→2スロット、80分→3スロット）
            </div>
          </div>

          {/* キャパシティ */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.75 }}>
              定員（キャパシティ） * — 整数で入力
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={capacityStr}
              onChange={(e) =>
                setCapacityStr(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder="30"
              style={inputStyle}
            />
            {isEdit && paidCount > 0 && (
              <div style={{ fontSize: 11, color: "#ffb3b3", marginTop: 4 }}>
                ※すでに{paidCount}名の申込があります。定員を{paidCount}未満には変更できません。
              </div>
            )}
          </div>

          {/* 特別レッスン項目 */}
          {isSpecial && (
            <>
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>
                  特別レッスン番号（例: 01, 02）
                </label>
                <input
                  type="text"
                  value={specialNumber}
                  onChange={(e) => setSpecialNumber(e.target.value)}
                  placeholder="03"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.75 }}>
                  特別レッスンラベル
                </label>
                <input
                  type="text"
                  value={specialLabel}
                  onChange={(e) => setSpecialLabel(e.target.value)}
                  placeholder="特別レッスン ③"
                  style={inputStyle}
                />
              </div>
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 6,
              padding: "8px 16px",
              cursor: "pointer",
            }}
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: "#2c7be5",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {submitting ? "送信中..." : isEdit ? "保存" : "作成"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: "rgba(11,13,26,0.7)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  marginTop: 4,
  boxSizing: "border-box",
};

// 「部屋を追加」(.room-add) と同じサイズ・フォントのベーススタイル
// 色味は呼び出し側で上書きする
const roomAddBtnBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "5px 12px",
  background: "rgba(255, 255, 255, 0.04)",
  border: "1px dashed rgba(255,255,255,0.4)",
  borderRadius: 999,
  cursor: "pointer",
  fontFamily: "var(--font-display)",
  fontSize: "var(--t-xs)",
  letterSpacing: "var(--ls-eyebrow)",
  textTransform: "uppercase",
  transition: "all 0.15s ease",
};

export default DanceCamp26ShiftPage;
